import { isRecord } from "containers/admin/clients/client/components/form-builder/form-builder-utils";
import {
  getFormSchemaTabs,
  normalizeJsonFormSchema,
  type DynamicFormSchema,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";

export type ProgramStrategyFormTab = {
  id: string;
  label: string;
  schema: DynamicFormSchema;
  dataPath: string[];
};

export type StrategyTabFieldOverrides = Record<
  string,
  Partial<Pick<JsonFormSchema, "x-disabled" | "description" | "x-options" | "x-disabled-options">>
>;

// Keyed by tab id, so an override targets only the tab it is meant for — a bare field name
// (e.g. "classes") would otherwise also match an unrelated field of the same name in any other tab.
export type StrategyFieldOverrides = Record<string, StrategyTabFieldOverrides>;

export const isDynamicFormSchema = (value: unknown): value is DynamicFormSchema =>
  isRecord(value) && value.type === "object" && isRecord(value.properties);

const getTabDataPath = (tab: { id: string; dataPath?: string[] }): string[] =>
  tab.dataPath ?? [tab.id];

export const getStrategyData = (data: unknown): Record<string, unknown> =>
  isRecord(data) ? data : {};

const applyFieldOverridesToField = (
  field: JsonFormSchema,
  fieldOverrides: StrategyTabFieldOverrides,
): JsonFormSchema => ({
  ...field,
  ...(field.properties
    ? { properties: applyFieldOverridesToProperties(field.properties, fieldOverrides) }
    : {}),
  ...(field.items ? { items: applyFieldOverridesToField(field.items, fieldOverrides) } : {}),
});

const applyFieldOverridesToProperties = (
  properties: Record<string, JsonFormSchema>,
  fieldOverrides: StrategyTabFieldOverrides,
): Record<string, JsonFormSchema> =>
  Object.fromEntries(
    Object.entries(properties).map(([key, field]) => [
      key,
      applyFieldOverridesToField(
        fieldOverrides[key] ? { ...field, ...fieldOverrides[key] } : field,
        fieldOverrides,
      ),
    ]),
  );

// Runs on an already-`normalizeJsonFormSchema`d schema, so nested fields reached only
// through a `$ref` (e.g. the shared student/parent creative item) are real properties by
// this point, not `{ $ref }` pointers.
const applyFieldOverrides = (
  schema: DynamicFormSchema,
  fieldOverrides: StrategyTabFieldOverrides,
): DynamicFormSchema => ({
  ...schema,
  properties: applyFieldOverridesToProperties(schema.properties, fieldOverrides),
});

export const createStrategyFormTabs = (
  schema: DynamicFormSchema,
  fieldOverrides?: StrategyFieldOverrides,
): ProgramStrategyFormTab[] => {
  const schemaTabs = getFormSchemaTabs(schema);

  if (schemaTabs.length > 0) {
    return schemaTabs.map((tab) => {
      const tabOverrides = fieldOverrides?.[tab.id];

      return {
        id: tab.id,
        label: tab.label,
        schema: tabOverrides
          ? applyFieldOverrides(normalizeJsonFormSchema(tab.schema), tabOverrides)
          : tab.schema,
        dataPath: getTabDataPath(tab),
      };
    });
  }

  const tabOverrides = fieldOverrides?.["program-strategy"];

  return [
    {
      id: "program-strategy",
      label: "Program strategy",
      schema: tabOverrides
        ? applyFieldOverrides(normalizeJsonFormSchema(schema), tabOverrides)
        : schema,
      dataPath: ["program-strategy"],
    },
  ];
};
